import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  applied: "Applied · waiting",
  pending_docs: "Pending docs",
  approved: "✓ Approved",
  denied: "Denied",
  frozen: "Frozen",
  closed: "Closed",
};

const STATUS_COLOR = {
  applied:      { bg: "rgba(245,158,11,0.15)", fg: "#FCD34D", bd: "#F59E0B" },
  pending_docs: { bg: "rgba(167,139,250,0.15)", fg: "#C4B5FD", bd: "#A78BFA" },
  approved:     { bg: "rgba(0,220,120,0.15)",   fg: "#4ADE80", bd: "#00DC78" },
  denied:       { bg: "rgba(239,68,68,0.15)",   fg: "#FCA5A5", bd: "#EF4444" },
  frozen:       { bg: "rgba(90,200,216,0.15)",  fg: "#5AC8D8", bd: "#5AC8D8" },
  closed:       { bg: "rgba(255,255,255,0.08)", fg: "#7E92A8", bd: "rgba(255,255,255,0.2)" },
};

const ACCOUNT_TYPE_LABEL = {
  business_checking:    "Business Checking",
  business_savings:     "Business Savings",
  business_credit_card: "Business Credit Card",
  loc:                  "Line of Credit",
  sba_loan:             "SBA Loan",
  merchant_services:    "Merchant Services",
};

export default function BankTrackerModal({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suggestedBanks, setSuggestedBanks] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const [form, setForm] = useState({
    bank_name: "",
    custom_bank: "",
    account_type: "business_checking",
    status: "applied",
    notes: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [rr, sr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/banks`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/banks/suggestions`, { credentials: "include" }),
      ]);
      if (rr.ok) {
        const rd = await rr.json();
        setRows(rd.rows || []); setSummary(rd.summary || null);
      }
      if (sr.ok) {
        const sd = await sr.json();
        setSuggestedBanks(sd.suggested_banks || []);
        setAccountTypes(sd.account_types || []);
        setStates(sd.states || []);
      }
    } catch { setError("Could not load."); }
    setBusy(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    const bank_name = form.bank_name === "__custom__" ? form.custom_bank.trim() : form.bank_name;
    if (!bank_name) { setError("Bank name required."); return; }
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/banks`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bank_name,
          account_type: form.account_type,
          status: form.status,
          notes: form.notes,
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone,
        }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.detail || "Failed.");
      else {
        setShowAdd(false);
        setForm({ bank_name: "", custom_bank: "", account_type: "business_checking", status: "applied", notes: "", contact_name: "", contact_email: "", contact_phone: "" });
        await load();
      }
    } catch { setError("Network error."); }
    setBusy(false);
  };

  const patch = async (application_id, patchBody) => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/banks/${application_id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBody),
      });
      if (!r.ok) { const d = await r.json(); setError(d.detail || "Update failed."); }
      else await load();
    } catch { setError("Network error."); }
    setBusy(false);
  };

  const del = async (application_id, bankLabel) => {
    if (!window.confirm(`Delete "${bankLabel}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/banks/${application_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (r.ok) await load();
    } catch { /* noop */ }
    setBusy(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="bank-tracker-backdrop"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.78)", backdropFilter: "blur(6px)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="bank-tracker-modal"
        style={{ background: "linear-gradient(180deg, #0F1729 0%, #0B1220 100%)", border: "1px solid rgba(184,145,90,0.35)", borderRadius: 14, padding: "1.5rem 1.6rem", maxWidth: 780, width: "100%", maxHeight: "92vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <p style={{ color: "#B8915A", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", margin: 0 }}>OPERATIONS · BANKING LEDGER</p>
            <h2 style={{ color: "#F3F4F6", fontSize: "1.35rem", fontWeight: 800, margin: "4px 0 6px" }}>🏦 Bank Application Tracker</h2>
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", margin: 0 }}>Every bank you&apos;ve applied to — approvals, denials, docs. Nothing falls through the cracks.</p>
          </div>
          <button data-testid="bank-tracker-close" onClick={onClose} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* Summary chips */}
        {summary && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <Chip label="Total" value={summary.total} color="rgba(255,255,255,0.06)" fg="#F3F4F6" />
            <Chip label="Approved" value={summary.approved} color={STATUS_COLOR.approved.bg} fg={STATUS_COLOR.approved.fg} />
            <Chip label="In review" value={summary.open_review} color={STATUS_COLOR.applied.bg} fg={STATUS_COLOR.applied.fg} />
            <Chip label="Denied" value={summary.denied} color={STATUS_COLOR.denied.bg} fg={STATUS_COLOR.denied.fg} />
          </div>
        )}

        {error && <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.82rem", marginBottom: 12 }}>{error}</div>}

        {/* Add form */}
        {showAdd ? (
          <div data-testid="bank-add-form" style={{ background: "rgba(15,20,30,0.6)", border: "1px solid rgba(184,145,90,0.3)", borderRadius: 10, padding: "1rem 1.15rem", marginBottom: 16 }}>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
              <Field label="Bank">
                <select data-testid="bank-add-bank-name" value={form.bank_name} onChange={(e) => setForm(f => ({ ...f, bank_name: e.target.value }))} style={inputStyle}>
                  <option value="">— Pick a bank —</option>
                  {suggestedBanks.map(b => <option key={b} value={b}>{b}</option>)}
                  <option value="__custom__">+ Other (type it)</option>
                </select>
                {form.bank_name === "__custom__" && (
                  <input data-testid="bank-add-custom" type="text" placeholder="Bank name" value={form.custom_bank} onChange={(e) => setForm(f => ({ ...f, custom_bank: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }} />
                )}
              </Field>
              <Field label="Account type">
                <select data-testid="bank-add-account-type" value={form.account_type} onChange={(e) => setForm(f => ({ ...f, account_type: e.target.value }))} style={inputStyle}>
                  {accountTypes.map(a => <option key={a} value={a}>{ACCOUNT_TYPE_LABEL[a] || a}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="bank-add-status" value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                  {states.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                </select>
              </Field>
              <Field label="Contact name (optional)">
                <input data-testid="bank-add-contact-name" type="text" value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="e.g. Sarah at Bluevine" style={inputStyle} />
              </Field>
              <Field label="Contact email (optional)">
                <input data-testid="bank-add-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} style={inputStyle} />
              </Field>
              <Field label="Contact phone (optional)">
                <input data-testid="bank-add-contact-phone" type="tel" value={form.contact_phone} onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))} style={inputStyle} />
              </Field>
            </div>
            <Field label="Notes (docs submitted, follow-up dates, denial reasons)">
              <textarea data-testid="bank-add-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: "vertical", minHeight: 60 }} />
            </Field>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button data-testid="bank-add-save" onClick={create} disabled={busy} style={btnPrimary}>{busy ? "Saving…" : "+ Add"}</button>
              <button onClick={() => setShowAdd(false)} style={btnGhost}>Cancel</button>
            </div>
          </div>
        ) : (
          <button
            data-testid="bank-tracker-add-toggle"
            onClick={() => setShowAdd(true)}
            style={{ ...btnPrimary, marginBottom: 16, width: "auto" }}
          >+ New Bank Application</button>
        )}

        {/* Rows */}
        {rows.length === 0 && !busy && (
          <div style={{ padding: 24, textAlign: "center", color: "#7E92A8", fontSize: "0.9rem" }}>
            No applications yet. Add your first one above.
          </div>
        )}

        <div style={{ display: "grid", gap: 10 }}>
          {rows.map(r => {
            const color = STATUS_COLOR[r.status] || STATUS_COLOR.applied;
            const expanded = expandedId === r.application_id;
            return (
              <div key={r.application_id} data-testid={`bank-row-${r.application_id}`} style={{ background: "rgba(15,20,30,0.5)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.85rem 1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <span style={{ fontSize: "1rem", fontWeight: 800, color: "#F3F4F6" }}>{r.bank_name}</span>
                      <span style={{ background: color.bg, color: color.fg, border: `1px solid ${color.bd}`, padding: "2px 8px", borderRadius: 999, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.04em" }}>{STATUS_LABEL[r.status] || r.status}</span>
                    </div>
                    <div style={{ color: "#ADBED1", fontSize: "0.78rem" }}>
                      {ACCOUNT_TYPE_LABEL[r.account_type] || r.account_type} · applied {new Date(r.applied_at).toLocaleDateString()}
                    </div>
                    {r.notes && <div style={{ color: "#7E92A8", fontSize: "0.78rem", marginTop: 4, whiteSpace: "pre-wrap" }}>{r.notes}</div>}
                    {r.denial_reason && <div style={{ color: "#FCA5A5", fontSize: "0.78rem", marginTop: 4 }}>Denial: {r.denial_reason}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexDirection: "column", alignItems: "flex-end" }}>
                    <select
                      data-testid={`bank-row-${r.application_id}-status`}
                      value={r.status}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        let denial = "";
                        if (newStatus === "denied") denial = window.prompt("Denial reason (optional):", "") || "";
                        patch(r.application_id, { status: newStatus, denial_reason: denial });
                      }}
                      style={{ ...inputStyle, padding: "0.35rem 0.5rem", fontSize: "0.75rem", width: "auto" }}
                    >
                      {states.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setExpandedId(expanded ? "" : r.application_id)} data-testid={`bank-row-${r.application_id}-toggle`} style={btnSmall}>{expanded ? "Hide" : "History"}</button>
                      <button onClick={() => del(r.application_id, r.bank_name)} data-testid={`bank-row-${r.application_id}-delete`} style={{ ...btnSmall, color: "#FCA5A5", borderColor: "rgba(239,68,68,0.35)" }}>🗑</button>
                    </div>
                  </div>
                </div>
                {expanded && (
                  <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(6,12,20,0.5)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>
                    <p style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.06em", margin: "0 0 6px" }}>HISTORY</p>
                    {(r.history || []).map((h, i) => (
                      <div key={i} style={{ fontSize: "0.74rem", color: "#ADBED1", padding: "3px 0", borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
                        {new Date(h.ts).toLocaleString()} · <strong style={{ color: color.fg }}>{STATUS_LABEL[h.status] || h.status}</strong>
                        {h.note && <span style={{ color: "#7E92A8" }}> — {h.note}</span>}
                        {h.by && <span style={{ color: "#7E92A8" }}> (by {h.by})</span>}
                      </div>
                    ))}
                    {(r.contact_name || r.contact_email || r.contact_phone) && (
                      <p style={{ color: "#ADBED1", fontSize: "0.74rem", marginTop: 8 }}>
                        📞 {r.contact_name || "—"} · {r.contact_email || "—"} · {r.contact_phone || "—"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const Chip = ({ label, value, color, fg }) => (
  <div style={{ background: color, color: fg, padding: "6px 12px", borderRadius: 8, fontSize: "0.75rem", fontWeight: 700 }}>
    <span style={{ opacity: 0.75 }}>{label}: </span><strong>{value}</strong>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: "block", color: "#ADBED1", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4, textTransform: "uppercase" }}>{label}</label>
    {children}
  </div>
);

const inputStyle = { width: "100%", background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, padding: "0.55rem 0.75rem", color: "#F3F4F6", fontSize: "0.88rem", outline: "none", boxSizing: "border-box" };
const btnPrimary = { background: "linear-gradient(90deg, #B8915A, #9A7845)", color: "#1a1206", border: "none", borderRadius: 8, padding: "0.7rem 1.1rem", fontWeight: 800, fontSize: "0.88rem", cursor: "pointer" };
const btnGhost = { background: "transparent", color: "#ADBED1", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "0.7rem 1rem", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" };
const btnSmall = { background: "transparent", color: "#ADBED1", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "0.3rem 0.55rem", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" };
